import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { ComplianceChecklist } from '../components/ComplianceChecklist';
import { gitlabApi, type GitlabConnectResult } from '../lib/gitlab-api';

/**
 * GitlabCallbackPage — the OAuth return target (GITLAB_OAUTH_REDIRECT_URI =
 * /onboarding/gitlab/cb). GitLab redirects here with ?code&state; we POST them
 * to /api/gitlab/oauth/callback exactly once, then render the result
 * (connected / blocked-with-fix-its). Firebase-authed (ProtectedRoute) so the
 * bearer token is available; no staff shell needed.
 */
export function GitlabCallbackPage() {
  const [params] = useSearchParams();
  const code = params.get('code');
  const state = params.get('state');
  const [result, setResult] = useState<GitlabConnectResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    if (!code || !state) {
      setError('Missing code/state in the GitLab redirect.');
      return;
    }
    handled.current = true;
    gitlabApi
      .callback(code, state)
      .then(setResult)
      .catch((e: Error) => setError(e.message));
  }, [code, state]);

  return (
    <div className="mx-auto max-w-lg space-y-4 p-10" data-testid="gitlab-callback">
      <h1 className="text-xl font-semibold text-foreground">{'Connecting your GitLab'}</h1>

      {!result && !error ? (
        <p className="text-sm text-muted-foreground">{'Finishing up…'}</p>
      ) : null}

      {error ? (
        <div className="rounded-md border border-destructive/40 bg-destructive-soft px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      ) : null}

      {result?.status === 'connected' ? (
        <div className="space-y-3" data-testid="gitlab-callback-connected">
          <div className="rounded-md border border-success/40 bg-success-soft px-3 py-2 text-sm text-success">
            {'Connected as '}
            <span className="font-mono font-medium">{`@${result.gitlabUsername}`}</span>
            {' — Developer access granted on the optisigns group.'}
          </div>
        </div>
      ) : null}

      {result?.status === 'blocked' ? (
        <div className="space-y-3" data-testid="gitlab-callback-blocked">
          <div className="rounded-md border border-warning/40 bg-warning-bg px-3 py-2 text-sm text-warning">
            {'No access was granted — your GitLab account must meet all of these:'}
          </div>
          <ComplianceChecklist reasons={result.reasons} />
        </div>
      ) : null}

      {result?.status === 'connected' ? (
        <ComplianceChecklist />
      ) : null}

      <Link
        to="/staff/onboarding"
        className="inline-flex items-center rounded-md border px-4 py-2 text-sm font-medium text-foreground hover:bg-panel-hover"
      >
        {'Back to Onboarding'}
      </Link>
    </div>
  );
}
